import { ResumeUploadService } from '@/services/resume-upload-service'
import { Track, type Section } from '@/services/track'
import { EphimeralService } from '@/utils/ephimeral'
import { prop } from '@tempots/dom'
import { ticker } from '@tempots/ui'
import { LatestResume } from './latest-resume'
import { Notify } from '@rh/shared/utils/notify'

export type UploadOptions = {
  trigger?: 'dragdrop' | 'click'
  section?: Section
}

export type ResumeNotAsked = {
  type: 'notAsked'
}

export type ResumeUplodaded = {
  type: 'uploaded'
  hash: string
  filename: string
  /**
   * True when hydrated from a previous session's latestResume instead of
   * an upload the user just made — consumers like auto-download must not
   * treat a page (re)mount as a new resume.
   */
  restored?: boolean
}

export type ResumeUploading = {
  type: 'uploading'
  file: File
}

export type ResumeError = {
  type: 'error'
  error: string
}

export type DefaultResume = {
  type: 'default'
}

export type ResumeConverting = {
  type: 'converting'
  hash: string
  filename: string
}

export type ResumeState =
  | ResumeNotAsked
  | DefaultResume
  | ResumeUplodaded
  | ResumeUploading
  | ResumeConverting
  | ResumeError

export const ResumeState = {
  notAsked: { type: 'notAsked' } as ResumeNotAsked,
  default: { type: 'default' } as DefaultResume,
  uploaded: (hash: string, filename: string): ResumeUplodaded => ({
    type: 'uploaded',
    hash,
    filename,
  }),
  uploading: (file: File): ResumeUploading => ({ type: 'uploading', file }),
  converting: (hash: string, filename: string): ResumeConverting => ({
    type: 'converting',
    hash,
    filename,
  }),
  error: (error: string): ResumeError => ({ type: 'error', error }),

  upload: async (
    file: File | null,
    onChange: (state: ResumeState) => void,
    options?: UploadOptions
  ) => {
    if (file == null) {
      onChange(ResumeState.notAsked)
      return
    }
    if (options?.section) {
      Track.upload({ trigger: options.trigger, section: options.section })
    }
    try {
      onChange(ResumeState.uploading(file))
      const hash = await ResumeUploadService.upload(file)

      const isPdf = file.type === 'application/pdf'
      if (isPdf) {
        onChange(ResumeState.uploaded(hash, file.name))
        Notify.success('Upload complete!')
      } else {
        // Non-PDF file: need to convert to PDF
        onChange(ResumeState.converting(hash, file.name))
        try {
          await ResumeUploadService.downloadPdfByHash(hash)
          onChange(ResumeState.uploaded(hash, file.name))
          Notify.success('Upload complete!')
        } catch (conversionError) {
          const conversionMessage =
            (conversionError as Error).message || 'PDF conversion failed'
          onChange(ResumeState.error(conversionMessage))
          Notify.error(`❌ ${conversionMessage}`)
          if (options?.section) {
            Track.uploaded({
              status: 'fail',
              file_type: file.type,
              file_size: file.size,
              error_code: 'CONVERSION_FAILED',
            })
          }
          return
        }
      }

      if (options?.section) {
        Track.uploaded({
          status: 'success',
          file_type: file.type,
          file_size: file.size,
        })
      }
    } catch (error) {
      const message = (error as Error).message || 'Upload failed'
      onChange(ResumeState.error(message))
      Notify.error(`❌ ${message}`)
      if (options?.section) {
        Track.uploaded({
          status: 'fail',
          file_type: file.type,
          file_size: file.size,
          error_code: 'UPLOAD_FAILED',
        })
      }
    }
  },
  makeUpload:
    (onChange: (state: ResumeState) => void, options?: UploadOptions) =>
    (file: File | null) =>
      ResumeState.upload(file, onChange, options),

  isDefault: (state: ResumeState): state is DefaultResume =>
    state.type === 'default',
  isUploaded: (state: ResumeState): state is ResumeUplodaded =>
    state.type === 'uploaded',
  isUploading: (state: ResumeState): state is ResumeUploading =>
    state.type === 'uploading',
  isNotAsked: (state: ResumeState): state is ResumeNotAsked =>
    state.type === 'notAsked',
  isConverting: (state: ResumeState): state is ResumeConverting =>
    state.type === 'converting',
  isError: (state: ResumeState): state is ResumeError => state.type === 'error',

  makeResumeState: ({
    resumeFile,
    setResumeFile,
    autoload = true,
    trackingOptions,
  }: {
    resumeFile?: File | null
    setResumeFile?: (file: File | null) => void
    autoload?: boolean
    trackingOptions?: UploadOptions
  }) => {
    const tick = ticker()
    const resume = prop<ResumeState>(ResumeState.notAsked)
    if (resumeFile != null) {
      ResumeState.upload(resumeFile, v => resume.set(v), trackingOptions)
      setResumeFile?.(null)
    } else if (autoload) {
      const latestResume =
        EphimeralService.retrieve<LatestResume>('latestResume')
      if (latestResume != null) {
        ResumeUploadService.checkExists(latestResume.hash).then(exists => {
          if (exists) {
            resume.set({
              ...ResumeState.uploaded(latestResume.hash, latestResume.filename),
              restored: true,
            })
          } else {
            EphimeralService.remove('latestResume')
          }
        })
      }
    }
    return { resume, tick }
  },
}
